/**
 * DEC-001: Shopify-style Experience View for Pricing Rule DocType
 * 
 * This view creates a Shopify-like pricing rule detail page with a Details tab.
 * 
 * Details Tab Sections:
 * - pricing-rule-basic: Basic pricing rule information (title, apply on, applicable for)
 * - pricing-rule-discount: Discount and rate information
 * - pricing-rule-validity: Validity period and priority
 * - pricing-rule-context: Context-specific fields (customer, item, company, etc.)
 */

import type { DocTypeView } from '../types';

export const pricingRuleShopifyView: DocTypeView = {
  id: 'pricing-rule-shopify-v1',
  doctype: 'Pricing Rule',
  style: 'shopify',
  context: undefined,
  status: 'active',
  label: 'Pricing Rule – Shopify Style',
  description: 'Shopify-style layout for Pricing Rule detail page with Details tab',
  formView: {
    sections: [
      // 1. Basic Information
      {
        id: 'pricing-rule-basic',
        title: 'Basic Information',
        description: 'Pricing rule identification and application settings',
        layout: [
          {
            columns: [
              {
                fieldname: 'name',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'title',
                mode: 'edit',
                visibility: 'default',
                width: '2/3',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'apply_on',
                mode: 'edit',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'applicable_for',
                mode: 'edit',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'price_or_product_discount',
                mode: 'edit',
                visibility: 'default',
                width: '1/3',
              },
            ],
          },
        ],
      },

      // 2. Discount & Rate Information
      {
        id: 'pricing-rule-discount',
        title: 'Discount & Rate',
        description: 'Discount and rate configuration',
        layout: [
          {
            columns: [
              {
                fieldname: 'rate_or_discount',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'currency',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'discount_percentage',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'rate',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
        ],
      },

      // 3. Validity & Priority
      {
        id: 'pricing-rule-validity',
        title: 'Validity & Priority',
        description: 'Validity period and priority settings',
        layout: [
          {
            columns: [
              {
                fieldname: 'valid_from',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'valid_upto',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'priority',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'selling',
                mode: 'edit',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'buying',
                mode: 'edit',
                visibility: 'default',
                width: '1/3',
              },
            ],
          },
        ],
      },

      // 4. Context (Customer, Item, Company)
      {
        id: 'pricing-rule-context',
        title: 'Context',
        description: 'Context-specific pricing rule information',
        layout: [
          {
            columns: [
              {
                fieldname: 'customer',
                mode: 'edit',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'customer_group',
                mode: 'edit',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'company',
                mode: 'edit',
                visibility: 'default',
                width: '1/3',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'item_code',
                mode: 'edit',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'item_group',
                mode: 'edit',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'brand',
                mode: 'edit',
                visibility: 'default',
                width: '1/3',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'warehouse',
                mode: 'edit',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },
    ],
    customFieldBucket: {
      enabled: true,
      sectionTitle: 'Additional ERP Fields',
      visibilityDefault: 'collapsed',
    },
  },
  createdBy: 'system',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

